
import { useCallback, useEffect, useRef } from "react";
import { type CarouselApi } from "@/components/ui/carousel/carousel-context";

interface UseCarouselAutoplayProps {
  api: CarouselApi | undefined;
  delay?: number;
  stopOnInteraction?: boolean;
}

export const useCarouselAutoplay = ({
  api,
  delay = 4000,
  stopOnInteraction = false,
}: UseCarouselAutoplayProps) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearAutoplay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    clearAutoplay();
    
    if (!api) return;

    intervalRef.current = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0); // Vuelve al inicio si está en el final
      }
    }, delay);
  }, [api, delay, clearAutoplay]);

  // Iniciar autoplay cuando el carousel API está disponible
  useEffect(() => {
    if (!api) return;
    startAutoplay();

    return () => clearAutoplay();
  }, [api, startAutoplay, clearAutoplay]);

  // Opcional: detener cuando el usuario interactúa
  useEffect(() => {
    if (!api || !stopOnInteraction) return;

    const onPointerDown = () => {
      clearAutoplay();
    };

    const onPointerUp = () => {
      startAutoplay();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("pointerup", onPointerUp);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [api, stopOnInteraction, startAutoplay, clearAutoplay]);

  return { startAutoplay, clearAutoplay };
};
